export enum MarketingRole {
  OWNER = 'OWNER',
  MANAGER = 'MANAGER',
  REP = 'REP',
}

/** Mirror of the backend's hierarchical roles guard: a requirement is
 * satisfied by the required role or anything above it. */
export const MARKETING_ROLE_RANK: Record<string, number> = {
  OWNER: 3,
  MANAGER: 2,
  REP: 1,
};

export function hasMarketingRole(
  userRole: string | undefined,
  required: MarketingRole,
): boolean {
  if (!userRole) return false;
  return (MARKETING_ROLE_RANK[userRole] ?? 0) >= (MARKETING_ROLE_RANK[required] ?? Infinity);
}

export enum LeadStatus {
  NEW = 'NEW',
  CONTACTED = 'CONTACTED',
  NOT_REACHABLE = 'NOT_REACHABLE',
  MEETING_DONE = 'MEETING_DONE',
  DEMO_SCHEDULED = 'DEMO_SCHEDULED',
  OFFER_SENT = 'OFFER_SENT',
  WAITING = 'WAITING',
  WON = 'WON',
  LOST = 'LOST',
}

export enum BusinessType {
  CAFE = 'CAFE',
  RESTAURANT = 'RESTAURANT',
  BAR = 'BAR',
  PATISSERIE = 'PATISSERIE',
  FAST_FOOD = 'FAST_FOOD',
  OTHER = 'OTHER',
}

export enum LeadSource {
  INSTAGRAM = 'INSTAGRAM',
  REFERRAL = 'REFERRAL',
  FIELD_VISIT = 'FIELD_VISIT',
  ADS = 'ADS',
  WEBSITE = 'WEBSITE',
  PHONE = 'PHONE',
  OTHER = 'OTHER',
  AI_RESEARCH = 'AI_RESEARCH',
  HARDWARE_QUOTE = 'HARDWARE_QUOTE',
  // System-set on CSV-import rows with no source column (backend import.service).
  IMPORT = 'IMPORT',
  // System-set when someone writes to the workspace's own inbox (backend
  // conversation-ingress.service).
  EMAIL = 'EMAIL',
}

export enum ActivityType {
  CALL = 'CALL',
  VISIT = 'VISIT',
  NOTE = 'NOTE',
  EMAIL = 'EMAIL',
  WHATSAPP = 'WHATSAPP',
  STATUS_CHANGE = 'STATUS_CHANGE',
  DEMO = 'DEMO',
  MEETING = 'MEETING',
}

export enum TaskType {
  CALL = 'CALL',
  VISIT = 'VISIT',
  DEMO = 'DEMO',
  FOLLOW_UP = 'FOLLOW_UP',
  MEETING = 'MEETING',
  OTHER = 'OTHER',
}

export enum TaskStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

export enum OfferStatus {
  DRAFT = 'DRAFT',
  SENT = 'SENT',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  EXPIRED = 'EXPIRED',
}

export interface MarketingUserInfo {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
}

export interface Lead {
  id: string;
  businessName: string;
  companyId?: string | null;
  /**
   * The B2B account behind `companyId`, resolved by the server on every list
   * read. NULL — not undefined — when the person is unlinked OR when the id
   * names nothing this workspace can see: `Company` has no foreign key back to
   * `Lead`, so the name is a lookup, and a lookup that misses must not be
   * papered over with a raw uuid. Absent (undefined) only on payloads that
   * predate the field.
   *
   * It is what the person surface's "Grupla: Şirkete göre" heads each block
   * with. The ORDER those blocks arrive in is the server's (`sortBy=company`);
   * this field only names them.
   */
  company?: { id: string; name: string } | null;
  contactPerson: string;
  phone?: string;
  /** NetGSM SMS v2 Task 12 — stamped when a rep confirms a live SMS OTP sent
   *  to `phone`; null/undefined = unverified. Cleared when `phone` is edited. */
  phoneVerifiedAt?: string | null;
  whatsapp?: string;
  email?: string;
  /** "Do not call/SMS me." Set by the consent panel, by campaign unsubscribes
   *  and by esp-feedback on a hard bounce; honoured by campaigns, by the AI
   *  auto-reply engine, by `POST /conversations/start` and (since the Ara
   *  button landed on the lead header) by `SalesCallService.startCall`. It
   *  rides along on `GET /marketing/leads/:id`, which returns the Lead row's
   *  scalars unfiltered. */
  smsOptOut?: boolean;
  /** "Do not email me." The email mirror of `smsOptOut` — set by the consent
   *  panel, by a campaign unsubscribe and by an ESP complaint. Rides along on
   *  `GET /marketing/leads/:id` with the rest of the Lead row's scalars.
   *  UNDEFINED IS NOT FALSE: a caller that renders a chip must test for an
   *  explicit `true`, or a payload that predates the field prints "opted out"
   *  over somebody who never did. */
  emailOptOut?: boolean;
  /** When this address last hard-bounced or drew a complaint. Non-null means
   *  the mail gateway will refuse it; null/undefined means nothing is known. */
  emailBouncedAt?: string | null;
  /** Hygiene verdict for `email`: 'UNKNOWN' until something checks it, then
   *  'VALID' / 'INVALID'. Only 'INVALID' blocks a send. */
  emailVerifiedStatus?: string | null;
  address?: string;
  city?: string;
  region?: string;
  businessType: string;
  tableCount?: number;
  branchCount?: number;
  currentSystem?: string;
  source: string;
  status: LeadStatus;
  lostReason?: string;
  notes?: string;
  nextFollowUp?: string;
  priority: string;
  assignedToId?: string | null;
  /**
   * Who owns this lead. NULL — not undefined — when nobody does: every producer
   * of a Lead includes the relation, and Prisma returns a missing relation as
   * null. The distinction is load-bearing on the person surface's record card,
   * where "Atanmamış" is an ANSWER (it is what the Atanmamış queue is about)
   * and must not be printed for a person whose record simply has not been read
   * yet. See `RecordCardLead`.
   */
  assignedTo?: MarketingUserInfo | null;
  convertedTenantId?: string;
  convertedAt?: string;
  createdAt: string;
  updatedAt: string;
  _count?: { activities: number; offers: number; tasks: number };

  // ── Conversation fields, stitched on by GET /marketing/leads ──────────────
  // The data half of "one object, the person; a conversation is a field of the
  // person". `conversations` has no foreign key to `leads`, so the backend
  // computes these with raw SQL and attaches them to the page
  // (lead-activity-enrichment.ts). Optional here because every OTHER producer
  // of a Lead — the create response, the detail payload, a cached row — has
  // never carried them.

  /** Newest message across ALL of this person's threads; null when they have
   *  none, or have a thread nothing has been said in yet. */
  lastMessageAt?: string | null;
  /** One flattened line of that message's body, <= 161 chars. */
  lastMessagePreview?: string | null;
  /** Summed across the person's threads. Never null — 0 is a real answer. */
  unreadCount?: number;
  /**
   * The newest of `lastMessageAt`, the newest lead activity, and the lead's own
   * `createdAt` — so it is NEVER null, and a person nothing has happened to
   * still sorts by when they arrived. This is the person list's default order.
   */
  lastActivityAt?: string;
}

export interface LeadActivityAssignmentMetadata {
  kind: 'assignment';
  fromUserId: string | null;
  fromUserName?: string | null;
  toUserId: string | null;
  toUserName?: string | null;
  auto?: boolean;
  bulk?: boolean;
}

export interface LeadActivity {
  id: string;
  type: string;
  title: string;
  description?: string;
  outcome?: string;
  duration?: number;
  metadata?: LeadActivityAssignmentMetadata | Record<string, unknown> | null;
  leadId: string;
  createdById: string;
  createdBy: MarketingUserInfo;
  createdAt: string;
}

export interface MarketingTask {
  id: string;
  title: string;
  description?: string;
  type: string;
  status: string;
  priority: string;
  dueDate: string;
  completedAt?: string;
  leadId?: string;
  lead?: { id: string; businessName: string };
  assignedToId: string;
  assignedTo: MarketingUserInfo;
  createdAt: string;
}

export interface LeadOffer {
  id: string;
  planId?: string;
  /** Currency of the plan snapshot (customPrice/planMonthlyPrice are in it); the
   *  app bills TRY or USD (dual-currency packages). Null for a plan-less offer. */
  planCurrency?: string | null;
  customPrice?: number;
  discount?: number;
  trialDays?: number;
  notes?: string;
  status: string;
  validUntil?: string;
  sentAt?: string;
  respondedAt?: string;
  leadId: string;
  lead?: { id: string; businessName: string; contactPerson: string };
  createdById: string;
  createdBy: MarketingUserInfo;
  createdAt: string;
}

export interface Commission {
  id: string;
  amount: number;
  type: string;
  status: string;
  period: string;
  tenantId?: string;
  leadId?: string;
  notes?: string;
  marketingUserId: string;
  marketingUser: MarketingUserInfo;
  approvedAt?: string;
  paidAt?: string;
  createdAt: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export const LEAD_STATUS_LABELS: Record<LeadStatus, string> = {
  [LeadStatus.NEW]: 'New',
  [LeadStatus.CONTACTED]: 'Contacted',
  [LeadStatus.NOT_REACHABLE]: 'Not Reachable',
  [LeadStatus.MEETING_DONE]: 'Meeting Done',
  [LeadStatus.DEMO_SCHEDULED]: 'Demo Scheduled',
  [LeadStatus.OFFER_SENT]: 'Offer Sent',
  [LeadStatus.WAITING]: 'Waiting',
  [LeadStatus.WON]: 'Won',
  [LeadStatus.LOST]: 'Lost',
};

export const LEAD_STATUS_COLORS: Record<LeadStatus, string> = {
  [LeadStatus.NEW]: 'bg-blue-100 text-blue-800',
  [LeadStatus.CONTACTED]: 'bg-indigo-100 text-indigo-800',
  [LeadStatus.NOT_REACHABLE]: 'bg-orange-100 text-orange-800',
  [LeadStatus.MEETING_DONE]: 'bg-purple-100 text-purple-800',
  [LeadStatus.DEMO_SCHEDULED]: 'bg-cyan-100 text-cyan-800',
  [LeadStatus.OFFER_SENT]: 'bg-yellow-100 text-yellow-800',
  [LeadStatus.WAITING]: 'bg-gray-100 text-gray-800',
  [LeadStatus.WON]: 'bg-green-100 text-green-800',
  [LeadStatus.LOST]: 'bg-red-100 text-red-800',
};

export const BUSINESS_TYPE_LABELS: Record<BusinessType, string> = {
  [BusinessType.CAFE]: 'Cafe',
  [BusinessType.RESTAURANT]: 'Restaurant',
  [BusinessType.BAR]: 'Bar',
  [BusinessType.PATISSERIE]: 'Patisserie',
  [BusinessType.FAST_FOOD]: 'Fast Food',
  [BusinessType.OTHER]: 'Other',
};

export const LEAD_SOURCE_LABELS: Record<LeadSource, string> = {
  [LeadSource.INSTAGRAM]: 'Instagram',
  [LeadSource.REFERRAL]: 'Referral',
  [LeadSource.FIELD_VISIT]: 'Field Visit',
  [LeadSource.ADS]: 'Ads',
  [LeadSource.WEBSITE]: 'Website',
  [LeadSource.PHONE]: 'Phone',
  [LeadSource.OTHER]: 'Other',
  [LeadSource.AI_RESEARCH]: 'AI Research',
  [LeadSource.HARDWARE_QUOTE]: 'Hardware quote',
  [LeadSource.IMPORT]: 'Imported',
  [LeadSource.EMAIL]: 'Email',
};

// ── Installation ops (Faz 3 backend; UI Faz 6) ────────────────────
export enum InstallationStatus {
  REQUESTED = 'REQUESTED',
  SCHEDULED = 'SCHEDULED',
  IN_PROGRESS = 'IN_PROGRESS',
  DONE = 'DONE',
  CANCELLED = 'CANCELLED',
  NO_SHOW = 'NO_SHOW',
}

export enum InstallationWindow {
  MORNING = 'MORNING',
  AFTERNOON = 'AFTERNOON',
  FULL_DAY = 'FULL_DAY',
}

export interface InstallationCrew {
  id: string;
  name: string;
  active: boolean;
  dailyCapacity: number;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface InstallationTask {
  id: string;
  jobId: string;
  title: string;
  done: boolean;
  position: number;
  createdAt: string;
}

export interface InstallationJob {
  id: string;
  tenantId: string;
  leadId?: string | null;
  crewId?: string | null;
  status: InstallationStatus;
  scheduledDate?: string | null;
  scheduledWindow?: string | null;
  siteAddress?: string | null;
  siteCity?: string | null;
  contactName?: string | null;
  contactPhone?: string | null;
  notes?: string | null;
  requestedAt: string;
  scheduledAt?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  tasks: InstallationTask[];
}

export interface CrewAvailability {
  crew: InstallationCrew;
  booked: number;
  available: boolean;
}

export interface InstallationDashboard {
  byStatus: Record<string, number>;
  unscheduled: number;
  overdueSla: number;
  upcoming: InstallationJob[];
}

// Allowed status moves — mirrors the backend INSTALL_TRANSITIONS state machine.
export const INSTALLATION_TRANSITIONS: Record<InstallationStatus, InstallationStatus[]> = {
  [InstallationStatus.REQUESTED]: [InstallationStatus.CANCELLED],
  [InstallationStatus.SCHEDULED]: [
    InstallationStatus.IN_PROGRESS,
    InstallationStatus.CANCELLED,
    InstallationStatus.NO_SHOW,
  ],
  [InstallationStatus.IN_PROGRESS]: [InstallationStatus.DONE, InstallationStatus.CANCELLED],
  [InstallationStatus.DONE]: [],
  [InstallationStatus.CANCELLED]: [],
  [InstallationStatus.NO_SHOW]: [InstallationStatus.CANCELLED],
};

export const INSTALLATION_STATUS_LABELS: Record<InstallationStatus, string> = {
  REQUESTED: 'Requested',
  SCHEDULED: 'Scheduled',
  IN_PROGRESS: 'In Progress',
  DONE: 'Done',
  CANCELLED: 'Cancelled',
  NO_SHOW: 'No-show',
};

export const INSTALLATION_WINDOW_LABELS: Record<InstallationWindow, string> = {
  MORNING: 'Morning',
  AFTERNOON: 'Afternoon',
  FULL_DAY: 'Full Day',
};

// ── Telephony / sales calls (Faz 2 backend; UI Faz 6) ─────────────
export enum CallStatus {
  INITIATED = 'INITIATED',
  CONNECTED = 'CONNECTED',
  NO_ANSWER = 'NO_ANSWER',
  BUSY = 'BUSY',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
}

export interface SalesCall {
  id: string;
  // Phase 3 Task 2 — nullable: an INBOUND call to an extension that doesn't
  // match any MarketingUser.dahili has no rep to attribute it to (OUTBOUND
  // calls always set this from the acting rep).
  marketingUserId: string | null;
  marketingUser?: MarketingUserInfo;
  leadId?: string | null;
  direction: string;
  toPhone: string;
  providerId: string;
  status: CallStatus;
  externalCallId?: string | null;
  durationSec?: number | null;
  recordingUrl?: string | null;
  notes?: string | null;
  startedAt: string;
  endedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface StartCallResult {
  call: SalesCall;
  dialUri: string;
  mode?: 'click-to-dial' | 'api';
}

export const CALL_STATUS_LABELS: Record<CallStatus, string> = {
  INITIATED: 'In progress',
  CONNECTED: 'Connected',
  NO_ANSWER: 'No answer',
  BUSY: 'Busy',
  FAILED: 'Failed',
  CANCELLED: 'Cancelled',
};

// Outcomes a rep can log (INITIATED is the start state only).
export const CALL_OUTCOMES = ['CONNECTED', 'NO_ANSWER', 'BUSY', 'FAILED', 'CANCELLED'] as const;

// ── Sales targets / performance (Faz 4 backend; UI Faz 6) ─────────
export enum TargetMetric {
  WON_LEADS = 'WON_LEADS',
  COMMISSION_AMOUNT = 'COMMISSION_AMOUNT',
  CONNECTED_CALLS = 'CONNECTED_CALLS',
}

export interface SalesTarget {
  id: string;
  marketingUserId: string;
  marketingUser?: MarketingUserInfo;
  period: string;
  metric: string;
  targetValue: number | string;
  setById: string;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MetricPerformance {
  metric: string;
  target: number | null;
  actual: number;
  attainmentPct: number | null;
}

export interface TeamPerformanceRow {
  marketingUser: { id: string; firstName: string; lastName: string; role: string };
  metrics: MetricPerformance[];
}

export const TARGET_METRIC_LABELS: Record<TargetMetric, string> = {
  WON_LEADS: 'Won Leads',
  // No hardcoded currency symbol — the value is rendered via formatMoney in the
  // workspace/plan currency (often ₺), so a '($)' label contradicted it.
  COMMISSION_AMOUNT: 'Commission',
  CONNECTED_CALLS: 'Connected Calls',
};

export const TARGET_METRICS = ['WON_LEADS', 'COMMISSION_AMOUNT', 'CONNECTED_CALLS'] as const;
